import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
} from '@mui/material';
import { CleaningServices as CleaningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import {
  PRINTER_CLEANING_STEPS,
  PRINTER_CLEANING_VIDEO_URL,
  PRINTER_CLEANING_WAITING_SEGMENT,
  PRINTER_CLEANING_ROLLBACK_STEPS,
} from './printerCleaningSteps';

function extractYoutubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const match =
    url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/) || [];
  return match[1] || null;
}

function loadYoutubeIframeApi() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);

  if (window.__fzYoutubeApiPromise) return window.__fzYoutubeApiPromise;

  window.__fzYoutubeApiPromise = new Promise(resolve => {
    const existing = document.getElementById('fz-youtube-iframe-api');
    if (!existing) {
      const tag = document.createElement('script');
      tag.id = 'fz-youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }

    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };

    const checkInterval = setInterval(() => {
      if (window.YT && window.YT.Player) {
        clearInterval(checkInterval);
        resolve(window.YT);
      }
    }, 50);
  });

  return window.__fzYoutubeApiPromise;
}

function formatDurationMs(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function PrinterCleaningGuideModal({
  open,
  onClose,
  printerName,
  onConfirmStartCleaning,
  disabled,
  isCleaningActive,
}) {
  const prepSteps = PRINTER_CLEANING_STEPS;
  const rollbackSteps = PRINTER_CLEANING_ROLLBACK_STEPS;
  const flowSteps = useMemo(
    () => [...prepSteps, PRINTER_CLEANING_WAITING_SEGMENT, ...rollbackSteps],
    [prepSteps, rollbackSteps]
  );

  const [stage, setStage] = useState('prep'); // 'prep' | 'waiting' | 'rollback' | 'done'
  const [prepConfirmed, setPrepConfirmed] = useState(0);
  const [rollbackConfirmed, setRollbackConfirmed] = useState(0);
  const [cleaningStartedAt, setCleaningStartedAt] = useState(null);
  const [hasObservedCleaningActive, setHasObservedCleaningActive] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [startInFlight, setStartInFlight] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const [isYoutubeReady, setIsYoutubeReady] = useState(false);
  const [youtubeInitFailed, setYoutubeInitFailed] = useState(false);
  const playerContainerRef = useRef(null);
  const playerRef = useRef(null);
  const loopTimerRef = useRef(null);

  const isInPrep = stage === 'prep' && prepConfirmed < prepSteps.length;
  const isPrepCompleted = stage === 'prep' && prepConfirmed >= prepSteps.length;
  const isWaiting = stage === 'waiting';
  const isRollback = stage === 'rollback';
  const isDone = stage === 'done';

  const videoStep = useMemo(() => {
    if (stage === 'prep' && prepConfirmed < prepSteps.length) return prepSteps[prepConfirmed];
    if (stage === 'rollback' && rollbackConfirmed < rollbackSteps.length)
      return rollbackSteps[rollbackConfirmed];
    return null;
  }, [stage, prepConfirmed, rollbackConfirmed, prepSteps, rollbackSteps]);

  const activeStepIndex = useMemo(() => {
    if (isDone) return flowSteps.length - 1;
    if (isRollback) return prepSteps.length + 1 + Math.min(rollbackConfirmed, rollbackSteps.length);
    if (isWaiting) return prepSteps.length;
    return Math.min(prepConfirmed, Math.max(0, prepSteps.length - 1));
  }, [isDone, isRollback, isWaiting, flowSteps.length, prepSteps.length, rollbackConfirmed, rollbackSteps.length, prepConfirmed]);

  const isStepCompleted = idx => {
    if (isDone) return true;
    const waitingIdx = prepSteps.length;
    if (stage === 'prep') return idx < prepConfirmed;
    if (stage === 'waiting') return idx < waitingIdx;
    if (stage === 'rollback') {
      if (idx <= waitingIdx) return true;
      return idx < waitingIdx + 1 + rollbackConfirmed;
    }
    return false;
  };

  const requestClose = () => {
    if (isDone) {
      setExitConfirmOpen(false);
      setStartInFlight(false);
      setStage('prep');
      setPrepConfirmed(0);
      setRollbackConfirmed(0);
      setCleaningStartedAt(null);
      setHasObservedCleaningActive(false);
      onClose();
      return;
    }
    setExitConfirmOpen(true);
  };

  const handleClose = () => {
    setExitConfirmOpen(false);
    setStartInFlight(false);
    setStage('prep');
    setPrepConfirmed(0);
    setRollbackConfirmed(0);
    setCleaningStartedAt(null);
    setHasObservedCleaningActive(false);
    onClose();
  };

  const handleConfirmPrepStep = () => {
    setPrepConfirmed(prev => Math.min(prev + 1, prepSteps.length));
  };

  const handleConfirmRollbackStep = () => {
    setRollbackConfirmed(prev => Math.min(prev + 1, rollbackSteps.length));
  };

  const handleConfirmStart = async () => {
    if (startInFlight) return;
    setStartInFlight(true);
    setStage('waiting');
    setRollbackConfirmed(0);
    setCleaningStartedAt(Date.now());
    setHasObservedCleaningActive(isCleaningActive === true);
    try {
      await onConfirmStartCleaning?.();
    } catch (e) {
      setStage('prep');
      setPrepConfirmed(prepSteps.length);
      setRollbackConfirmed(0);
      setCleaningStartedAt(null);
      setHasObservedCleaningActive(false);
      throw e;
    } finally {
      setStartInFlight(false);
    }
  };

  const videoId = useMemo(() => extractYoutubeVideoId(PRINTER_CLEANING_VIDEO_URL), []);

  useEffect(() => {
    if (!open) return undefined;
    setStage(isCleaningActive ? 'waiting' : 'prep');
    setPrepConfirmed(isCleaningActive ? prepSteps.length : 0);
    setRollbackConfirmed(0);
    setCleaningStartedAt(isCleaningActive ? Date.now() : null);
    setHasObservedCleaningActive(isCleaningActive === true);
    setNowMs(Date.now());
    setStartInFlight(false);
    setExitConfirmOpen(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (stage !== 'prep') return;
    if (isCleaningActive !== true) return;
    setStage('waiting');
    setPrepConfirmed(prepSteps.length);
    setCleaningStartedAt(prev => prev ?? Date.now());
    setHasObservedCleaningActive(true);
  }, [open, stage, isCleaningActive, prepSteps.length]);

  useEffect(() => {
    if (!open) return undefined;
    if (!isWaiting) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 500);
    return () => clearInterval(timer);
  }, [open, isWaiting]);

  useEffect(() => {
    if (!open) return;
    if (!isWaiting) return;
    if (isCleaningActive === true) setHasObservedCleaningActive(true);
    if (hasObservedCleaningActive && isCleaningActive === false) setStage('rollback');
  }, [open, isWaiting, isCleaningActive, hasObservedCleaningActive]);

  useEffect(() => {
    if (stage !== 'rollback') return;
    if (rollbackConfirmed >= rollbackSteps.length) setStage('done');
  }, [stage, rollbackConfirmed, rollbackSteps.length]);

  useEffect(() => {
    if (!open) return;
    if (!videoId) return;
    let active = true;
    loadYoutubeIframeApi()
      .then(() => {
        if (!active) return;
        setIsYoutubeReady(true);
      })
      .catch(() => {
        if (!active) return;
        setYoutubeInitFailed(true);
      });
    return () => {
      active = false;
    };
  }, [open, videoId]);

  useEffect(() => {
    const start = videoStep?.videoTimestampSeconds ?? 0;
    const end = videoStep?.videoTimestampEndSeconds ?? null;

    const clearLoopTimer = () => {
      if (loopTimerRef.current) {
        clearInterval(loopTimerRef.current);
        loopTimerRef.current = null;
      }
    };

    if (!open || !isYoutubeReady || !videoId || !videoStep) {
      clearLoopTimer();
      try {
        playerRef.current?.pauseVideo?.();
      } catch (_) {}
      return undefined;
    }

    setYoutubeInitFailed(false);

    const ensurePlayer = async () => {
      try {
        const YT = await loadYoutubeIframeApi();
        if (!playerContainerRef.current) return;

        const onReady = e => {
          try {
            e.target.mute();
            e.target.playVideo();
          } catch (_) {}
        };

        const onStateChange = e => {
          try {
            if (end != null && e.data === YT.PlayerState.ENDED) {
              e.target.seekTo(start, true);
              e.target.playVideo();
            }
          } catch (_) {}
        };

        if (!playerRef.current) {
          playerRef.current = new YT.Player(playerContainerRef.current, {
            videoId,
            playerVars: {
              autoplay: 1,
              controls: 1,
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
              start,
              ...(end != null ? { end } : {}),
            },
            events: { onReady, onStateChange },
          });
        } else {
          playerRef.current.loadVideoById({
            videoId,
            startSeconds: start,
            ...(end != null ? { endSeconds: end } : {}),
          });
          try {
            playerRef.current.mute();
            playerRef.current.playVideo();
          } catch (_) {}
        }

        clearLoopTimer();
        loopTimerRef.current = setInterval(() => {
          try {
            const p = playerRef.current;
            if (!p || typeof p.getCurrentTime !== 'function') return;
            const t = p.getCurrentTime();
            if (typeof t !== 'number') return;

            if (start > 0 && t < start - 0.5) {
              p.seekTo(start, true);
              p.playVideo();
              return;
            }

            if (end != null && t >= end - 0.05) {
              p.seekTo(start, true);
              p.playVideo();
            }
          } catch (_) {}
        }, 250);
      } catch (_) {
        setYoutubeInitFailed(true);
      }
    };

    ensurePlayer();

    return () => {
      clearLoopTimer();
    };
  }, [open, isYoutubeReady, videoId, videoStep]);

  useEffect(() => {
    if (open) return undefined;
    if (loopTimerRef.current) {
      clearInterval(loopTimerRef.current);
      loopTimerRef.current = null;
    }
    if (playerRef.current && typeof playerRef.current.destroy === 'function') {
      try {
        playerRef.current.destroy();
      } catch (_) {}
    }
    playerRef.current = null;
    setIsYoutubeReady(false);
    setYoutubeInitFailed(false);
    return undefined;
  }, [open]);

  const waitingDurationMs = cleaningStartedAt ? nowMs - cleaningStartedAt : 0;
  const showVideo = Boolean(videoStep && (isInPrep || isRollback));
  const shouldRenderVideoShell = Boolean(open && videoId && !youtubeInitFailed);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          requestClose();
          return;
        }
        requestClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Druckerreinigung
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }} color="text.secondary">
          Drucker: <strong>{printerName || '-'}</strong>
        </Typography>

        <Stepper
          alternativeLabel
          activeStep={activeStepIndex}
          sx={{
            mb: 3,
            overflowX: 'hidden',
            pb: 1,
            '& .MuiStep-root': {
              flex: '1 1 0',
              minWidth: 0,
              px: 0.25,
            },
            '& .MuiStepLabel-label': {
              mt: 0.5,
              fontSize: 12,
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              display: 'block',
            },
            '& .MuiStepLabel-iconContainer': {
              p: 0,
            },
            '& .MuiStepConnector-line': {
              borderTopWidth: 2,
            },
          }}
        >
          {flowSteps.map((step, idx) => (
            <Step key={step.id} completed={isStepCompleted(idx)}>
              <StepLabel>{step.shortLabel || step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {isInPrep && videoStep && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Vorbereitung – Schritt {prepConfirmed + 1} von {prepSteps.length}: {videoStep.label}
            </Typography>
            {videoStep.id === 'slider-max' && (
              <Box
                sx={{
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'error.main',
                  backgroundColor: theme =>
                    theme.palette.mode === 'light'
                      ? 'rgba(211, 47, 47, 0.08)'
                      : 'rgba(244, 67, 54, 0.16)',
                }}
              >
                <Typography sx={{ color: 'error.main', fontWeight: 700 }}>Wichtig:</Typography>
                <Typography sx={{ color: 'error.main' }}>
                  Merke dir die ursprüngliche Position des Schiebereglers, damit du ihn nach der Reinigung wieder genauso einstellen kannst.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {shouldRenderVideoShell && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingBottom: showVideo ? '56.25%' : 0,
              height: showVideo ? 'auto' : 0,
              mb: showVideo ? 1 : 0,
              borderRadius: 1,
              overflow: 'hidden',
              border: showVideo ? '1px solid' : '0px solid',
              borderColor: 'divider',
              display: showVideo ? 'block' : 'none',
            }}
          >
            <Box
              ref={playerContainerRef}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                '& iframe': { width: '100%', height: '100%' },
              }}
            />
          </Box>
        )}

        {isPrepCompleted && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 2,
            }}
          >
            <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="h6" color="success.main">
              Alle Vorbereitungsschritte abgeschlossen
            </Typography>
            <Typography color="text.secondary">Du kannst jetzt die Reinigung starten.</Typography>
          </Box>
        )}

        {isWaiting && (
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {startInFlight ? 'Reinigung wird gestartet…' : 'Reinigung läuft…'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Bitte warten, bis der Drucker wieder bereit ist. Dauer:{' '}
              <strong>{formatDurationMs(waitingDurationMs)}</strong>
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {isRollback && videoStep && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Rückbau – Schritt {rollbackConfirmed + 1} von {rollbackSteps.length}: {videoStep.label}
            </Typography>
          </Box>
        )}

        {isDone && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 3,
              '@keyframes fz-pop': {
                '0%': { transform: 'scale(0.6)', opacity: 0 },
                '70%': { transform: 'scale(1.08)', opacity: 1 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          >
            <CheckCircleIcon
              color="success"
              sx={{ fontSize: 72, animation: 'fz-pop 520ms ease-out both' }}
            />
            <Typography variant="h6" color="success.main">
              Reinigung abgeschlossen
            </Typography>
            <Typography color="text.secondary">Der Drucker ist wieder einsatzbereit.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={requestClose}>{isDone ? 'Schließen' : 'Abbrechen'}</Button>
        {isInPrep ? (
          <Button variant="contained" onClick={handleConfirmPrepStep}>
            {videoStep?.label} – erledigt
          </Button>
        ) : isPrepCompleted ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmStart}
            disabled={disabled || startInFlight}
            startIcon={<CleaningIcon />}
          >
            Reinigung starten
          </Button>
        ) : isRollback ? (
          <Button variant="contained" onClick={handleConfirmRollbackStep}>
            {videoStep?.label} – erledigt
          </Button>
        ) : null}
      </DialogActions>

      <Dialog open={exitConfirmOpen} onClose={() => setExitConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Prozess verlassen?</DialogTitle>
        <DialogContent>
          <Typography>
            Wenn du das Fenster schließt, verlässt du den geführten Reinigungsprozess.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitConfirmOpen(false)}>Im Prozess bleiben</Button>
          <Button variant="contained" color="error" onClick={handleClose}>
            Prozess verlassen
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default PrinterCleaningGuideModal;
